import React, { useMemo, useState } from 'react';
import proof2 from '../assets/proof2.png';
import './FeeCalculator.css';

// Standard PayPal commercial transaction fee (US): 2.9% + $0.30
const PAYPAL_RATE = 0.029;
const PAYPAL_FIXED = 0.3;

interface PlatformResult {
  name: string;
  feeLabel: string;
  perSale: number;
  monthly: number;
  instant: boolean;
  highlight?: boolean;
}

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const FeeCalculator: React.FC = () => {
  const [price, setPrice] = useState('100');
  const [sales, setSales] = useState('10');

  const results = useMemo<PlatformResult[]>(() => {
    const p = Math.max(0, parseFloat(price) || 0);
    const s = Math.max(0, parseInt(sales, 10) || 0);
    const paypal = p * PAYPAL_RATE + PAYPAL_FIXED;

    // PokeShani: 0% platform fee, PayPal processing may apply, instant payout
    const pokeshani: PlatformResult = {
      name: 'PokeShani',
      feeLabel: 'Takes 0%, PayPal fee may apply',
      perSale: p - paypal,
      monthly: s * (p - paypal),
      instant: true,
      highlight: true,
    };

    // Gumroad: 10% + $0.50 per direct sale, PayPal fees can apply separately
    const gumroad: PlatformResult = {
      name: 'Gumroad',
      feeLabel: '10% + $0.50 per sale + PayPal fees separately',
      perSale: p - (p * 0.1 + 0.5) - paypal,
      monthly: s * (p - (p * 0.1 + 0.5) - paypal),
      instant: false,
    };

    // Lemon Squeezy comparison removed per request. Only Gumroad vs PokeShani.

    // Fixed order: PokeShani first (highlighted), then Gumroad
    return [pokeshani, gumroad];
  }, [price, sales]);

  const advantage = results[0].monthly - Math.max(
    ...results.filter((r) => !r.highlight).map((r) => r.monthly),
    0
  );

  return (
    <section className="fee-calc-section">
      <h2 className="section-title">See Exactly How Much You Keep</h2>
      <p className="section-subtitle">
        Enter your price and monthly sales. Live math, real published fees.
      </p>

      <div className="fee-calc-layout">
        <div className="fee-calc-card">
          <div className="fee-calc-inputs">
            <label className="fee-calc-field">
              <span>Product price ($)</span>
              <div className="fee-calc-inputwrap">
                <span className="fee-calc-prefix">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. 25"
                />
              </div>
            </label>
            <label className="fee-calc-field">
              <span>Sales per month</span>
              <div className="fee-calc-inputwrap">
                <span className="fee-calc-prefix fee-calc-prefix-x">×</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={sales}
                  onChange={(e) => setSales(e.target.value)}
                  placeholder="e.g. 10"
                />
              </div>
            </label>
          </div>
          <p className="fee-calc-hint">
            Type your price and monthly sales above, the table updates instantly
          </p>

          <div className="fee-calc-table-wrap">
            <table className="fee-calc-table">
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Fees</th>
                  <th>You keep</th>
                  <th>Paid in 5s</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.name} className={r.highlight ? 'fee-row-highlight' : ''}>
                    <td className="fee-col-name"><strong>{r.name}</strong></td>
                    <td className="fee-col-fees">{r.feeLabel}</td>
                    <td className="fee-col-amount">
                      {fmt(r.perSale)}<span className="fee-amount-sub"> /sale</span>
                      <br />
                      <span className="fee-amount-monthly">{fmt(r.monthly)}<span className="fee-amount-sub"> /mo</span></span>
                    </td>
                    <td className={`fee-col-instant ${r.instant ? 'instant-yes' : 'instant-no'}`}>
                      {r.instant ? '✅' : '❌'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {advantage > 0.005 && (
            <div className="fee-calc-verdict">
              You keep {fmt(advantage)} more per month with PokeShani, and the
              money hits your PayPal in about 5 seconds. No threshold.
            </div>
          )}
        </div>

        <div className="fee-calc-visual">
          <div className="fee-calc-visual-frame">
            <img
              src={proof2}
              alt="PokeShani dashboard with advanced analytics tracking sales, visitors and revenue"
              className="fee-calc-visual-image"
            />
          </div>
          <span className="fee-calc-visual-caption">
            Advanced analytics to track sales, visitors and revenue
          </span>
        </div>
      </div>
    </section>
  );
};

export default FeeCalculator;